import React from 'react'
import styled from 'styled-components'
import { string, array, number, func } from 'prop-types'
import { stripUnit, rgba } from 'polished'
import ReactTooltip from 'react-tooltip'

import * as spacing from 'styles/spacing'
import { white, black, borderColor } from 'styles/colors'
import { media } from 'styles/media'
import { H3, fontWeights } from 'styles/typography'
import { cover, square } from 'styles/global'

import { StyledButton } from 'components/buy/BuySummary'

const Container = styled.div`
  position: sticky;
  bottom: 0;
  left: 0;
  right: 0;
  padding: ${spacing.medium} ${spacing.large};
  display: grid;
  grid-gap: ${spacing.medium};
  background-color: ${white};
  border-top: 1px solid ${borderColor};
  z-index: 20;
  text-align: center;

  ${media.medium`
    grid-template-columns: repeat(2, 1fr);
    grid-gap: 0;
    padding: ${spacing.large};
    margin-left: -50vw;
  `};
`

const Section = styled.div`
  position: static;
`

const Selection = styled.div`
  display: flex;
  justify-content: center;
`

const Label = styled.span`
  color: ${black};
  text-transform: uppercase;
  letter-spacing: 0.1em;
  display: block;
  margin-bottom: ${stripUnit(spacing.small) * 1.5 + 'px'};

  ${media.medium`
    font-size: 17px;
  `};

  ${media.large`
    font-size: 18px;
    margin-bottom: ${spacing.medium};
  `};

  ${media.xLarge`
    font-size: 20px;
  `};
`

const AddButton = styled(StyledButton)`
  position: static;

  ${media.medium`
    margin-left: 0 !important;
  `};
`

const ButtonWrap = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;

  ${media.medium`
    flex-direction: column;
  `};
`

const Price = styled(H3)`
  color: ${black};
  margin-bottom: 0;
  font-weight: ${fontWeights.medium};

  ${media.medium`
    margin-bottom: ${spacing.medium};
  `};
`

const FlavourContainer = styled.div`
  position: relative;
  border-radius: 50%;
  background-color: ${rgba(borderColor, 0.5)};
  ${square('40px')};

  ${media.small`
    ${square('48px')};
  `};

  ${media.medium`
    ${square('56px')};
  `};

  ${media.large`
    ${square('64px')};
  `};

  ${media.xLarge`
    ${square('80px')};
  `};

  & + & {
    margin-left: ${spacing.medium};

    ${media.medium`
      margin-left: ${spacing.large};
    `};

    ${media.xLarge`
      margin-left: ${spacing.xLarge};
    `};
  }
`

const FlavourFigure = styled.img`
  ${cover('absolute')};
  object-fit: cover;
  margin-top: -7.5%; /* Align image to circle */
`

const MultipackSummary = ({ flavours, saving, price, limit, onAdd }) => {
  let actionName = 'Add to cart'

  if (limit === 5) {
    if (flavours.length === 4) actionName = 'Add one more pack'
    if (flavours.length === 3) actionName = 'Add two more packs'
    if (flavours.length === 2) actionName = 'Add three more packs'
    if (flavours.length === 1) actionName = 'Add four more packs'
    if (flavours.length === 0) actionName = 'Add five packs'
  } else if (limit === 3) {
    if (flavours.length === 2) actionName = 'Add one more pack'
    if (flavours.length === 1) actionName = 'Add two more pack'
    if (flavours.length === 0) actionName = 'Add three packs'
  }

  return (
    <Container>
      <Section>
        <Label className="u-hide-medium-down">Your multipack</Label>
        <Selection>
          {[...Array(limit).keys()].map((e, i) => {
            const name = flavours[i]?.name || null
            const figure = flavours[i]?.figure || null

            return <Flavour key={i} name={name} figure={figure} />
          })}
        </Selection>
      </Section>
      <Section>
        <Label>You are saving {saving}</Label>
        <ButtonWrap>
          <Price>${price}</Price>
          <AddButton
            theme="primary"
            disabled={flavours.length < limit}
            onClick={() => {
              onAdd()
            }}
          >
            {actionName}
          </AddButton>
        </ButtonWrap>
      </Section>
    </Container>
  )
}

MultipackSummary.propTypes = {
  flavours: array,
  saving: string,
  price: string,
  limit: number,
  onAdd: func
}

const Flavour = ({ name, figure }) => (
  <>
    <FlavourContainer data-tip={name}>
      {figure && (
        <>
          <FlavourFigure src={figure} />
          <ReactTooltip effect="solid" className="tooltip" />
        </>
      )}
    </FlavourContainer>
  </>
)

Flavour.propTypes = {
  name: string,
  figure: string
}

export default MultipackSummary
